import { createIdentifier } from '@blocksuite/global/di';
import { IS_FIREFOX } from '@blocksuite/global/env';
import { LifeCycleWatcher } from '@blocksuite/std';
import type { ExtensionType } from '@blocksuite/store';

import type { FontConfig } from './config.js';

const initFontFace = IS_FIREFOX
  ? ({ font, weight, url, style }: FontConfig) =>
      new FontFace(`"${font}"`, `url(${url})`, {
        weight,
        style,
      })
  : ({ font, weight, url, style }: FontConfig) =>
      new FontFace(font, `url(${url})`, {
        weight,
        style,
      });

const getFontKey = ({ font, weight, style, url }: FontConfig) =>
  `${font}\u0000${weight}\u0000${style}\u0000${url}`;

const globalFontFaces = new Map<
  string,
  {
    fontFace: FontFace;
    refCount: number;
  }
>();

export class FontLoaderService extends LifeCycleWatcher {
  static override readonly key = 'font-loader';

  private readonly fontKeys: string[] = [];

  get ready() {
    return Promise.all(
      this.fontKeys
        .map(key => globalFontFaces.get(key)?.fontFace.loaded)
        .filter((loaded): loaded is Promise<FontFace> => !!loaded)
    );
  }

  load(fonts: FontConfig[]) {
    for (const font of fonts) {
      const key = getFontKey(font);
      let entry = globalFontFaces.get(key);
      if (!entry) {
        const fontFace = initFontFace(font);
        document.fonts.add(fontFace);
        fontFace.load().catch(console.error);
        entry = {
          fontFace,
          refCount: 0,
        };
        globalFontFaces.set(key, entry);
      }
      entry.refCount += 1;
      this.fontKeys.push(key);
    }
  }

  override mounted() {
    const config = this.std.getOptional(FontConfigIdentifier);
    if (config) {
      this.load(config);
    }
  }

  override unmounted() {
    this.fontKeys.forEach(key => {
      const entry = globalFontFaces.get(key);
      if (!entry) return;
      entry.refCount -= 1;
      if (entry.refCount <= 0) {
        document.fonts.delete(entry.fontFace);
        globalFontFaces.delete(key);
      }
    });
    this.fontKeys.splice(0, this.fontKeys.length);
  }
}

export const FontConfigIdentifier =
  createIdentifier<FontConfig[]>('AffineFontConfig');

export const FontConfigExtension = (
  fontConfig: FontConfig[]
): ExtensionType => ({
  setup: di => {
    di.addImpl(FontConfigIdentifier, () => fontConfig);
  },
});
